/**
 * "Back to where I came from" for boards and databases. A link to a resource carries the catalog
 * page it was clicked on (Home, a workspace, a category list) in the router's location state; the
 * resource page turns it into its back link. State (not `history.back()`) survives a reload, never
 * exists for a direct link or a new tab (those get the page's own fallback), and only in-app
 * catalog paths are honoured - an arbitrary string in the state can never become a link.
 * Distinct from `return-target.ts`, which brings Settings pages back to a card or board.
 */
export interface OriginState {
  origin: string;
}

export type OriginKind = 'home' | 'workspace' | 'category';

const MAX_LENGTH = 500;
// Home, one workspace (its sections and filters live in the query) or a category list.
const HOME = /^\/app\/?(\?[^#]*)?$/;
const WORKSPACE = /^\/app\/workspaces\/([^/?#]+)\/?(\?[^#]*)?$/;
const CATEGORY =
  /^\/app\/browse\/(boards|team-boards|workspaces|databases|favorites)\/?(\?[^#]*)?$/;

export function originKind(value: unknown): OriginKind | null {
  if (typeof value !== 'string' || value.length > MAX_LENGTH) return null;
  if (HOME.test(value)) return 'home';
  if (WORKSPACE.test(value)) return 'workspace';
  if (CATEGORY.test(value)) return 'category';
  return null;
}

export const isOriginPath = (value: unknown): value is string => originKind(value) !== null;

/** The workspace id of a workspace origin, else `null`. */
export function originWorkspaceId(path: string): string | null {
  const id = WORKSPACE.exec(path)?.[1];
  return id ? decodeURIComponent(id) : null;
}

/** The category of a category origin (`boards`, `team-boards`, ...), else `null`. */
export function originCategory(path: string): string | null {
  return CATEGORY.exec(path)?.[1] ?? null;
}

interface LocationLike {
  pathname: string;
  search: string;
  state: unknown;
}

export function readOrigin(location: { state: unknown }): string | null {
  const state = location.state;
  if (typeof state === 'object' && state !== null && 'origin' in state) {
    const { origin } = state;
    return isOriginPath(origin) ? origin : null;
  }
  return null;
}

/**
 * The state for a link to a resource, put on a link rendered at `location`: the page itself when
 * it is a catalog; an origin already carried (moving between resources) stays; otherwise nothing.
 */
export function originStateFrom(location: LocationLike): OriginState | undefined {
  const here = `${location.pathname}${location.search}`;
  if (isOriginPath(here)) return { origin: here };
  const inherited = readOrigin(location);
  return inherited ? { origin: inherited } : undefined;
}
